// BillingProgressGroup.jsx
"use client";

import React from "react";
import BillingProgressRing from "./BillingProgressRing";
import Tooltip from "../../shared-modules/Tooltip";
import { mapIndicatorToSegments } from "./billingMapper";
import { getLayerZIndex } from "../../../utils/layerManager";
import styles from "./billingProgress.module.css";


const StatusDot = ({ done, small = false }) => (
  <span
    style={{
      display: "inline-block",
      width: small ? 6 : 8,
      height: small ? 6 : 8,
      borderRadius: "50%",
      backgroundColor: done ? "#1FBF75" : "#F4A261",
      marginRight: 6,
      flexShrink: 0,
    }}
  />
);

const BillingProgressGroup = ({ rpm, ccm, isLight = false, isPatientDetail = false }) => {
  const rpmSegments = rpm ? mapIndicatorToSegments(rpm, "RPM") : null;
  const ccmSegments = ccm ? mapIndicatorToSegments(ccm, "CCM") : null;

  const hasRPM = Array.isArray(rpmSegments) && rpmSegments.length > 0;
  const hasCCM = Array.isArray(ccmSegments) && ccmSegments.length > 0;

  if (!hasRPM && !hasCCM) return null;

  // Tooltip styling based on context
  const tooltipPosition = isPatientDetail ? "bottom" : "top-left";
  const tooltipStyle = isPatientDetail
    ? { backgroundColor: "#10164d", color: "#fff", borderTop: "1px solid #fff", borderRight: "none", borderBottom: "none", borderLeft: "none", zIndex: getLayerZIndex("tooltip") }
    : { backgroundColor: "#10164d", color: "#fff", zIndex: getLayerZIndex("tooltip") };
  const tooltipCustomClass = isPatientDetail ? "patientDetailTooltip" : "";

  return (
    <div className={styles.group} style={{ display: "flex", gap: 8, alignItems: "center" }}>
      {hasRPM && (
        <Tooltip
          position={tooltipPosition}
          tooltipStyle={tooltipStyle}
          customClass={tooltipCustomClass}
          content={
            <div style={{ fontSize: 12, lineHeight: 1.5, textAlign: "left" }}>
              <div style={{ fontWeight: 600 }}>RPM</div>
              <div>
                <StatusDot done={rpmSegments[0]?.completed} /> Device readings (2+ days / 30 days)
              </div>
              <div>
                <StatusDot done={rpmSegments[1]?.completed} /> Two-way communication
              </div>
              <div>
                <StatusDot done={rpmSegments[2]?.completed} /> Time: 20m
              </div>
              <div>
                <StatusDot done={rpmSegments[3]?.completed} /> Time: 40m
              </div>
              <div>
                <StatusDot done={rpmSegments[4]?.completed} /> Time: 60m
              </div>
            </div>
          }
        >
          <BillingProgressRing label="RPM" segments={rpmSegments} isLight={isLight} indicator={rpm} />
        </Tooltip>
      )}

      {hasCCM && (
        <Tooltip
          position={tooltipPosition}
          tooltipStyle={tooltipStyle}
          customClass={tooltipCustomClass}
          content={
            <div style={{ fontSize: 12, lineHeight: 1.5, textAlign: "left" }}>
              <div style={{ fontWeight: 600 }}>CCM</div>
              <div>
                <StatusDot done={ccmSegments[0]?.completed} /> Two-Way Communication
              </div>
              <div>
                <StatusDot done={ccmSegments[0]?.completed} /> Care plan documented
              </div>
              <div>
                <StatusDot done={ccmSegments[1]?.completed} /> Care plan finalized
              </div>
              <div>
                <StatusDot done={ccmSegments[2]?.completed} /> Time: 40m
              </div>
              <div>
                <StatusDot done={ccmSegments[3]?.completed} /> Time: 60m
              </div>
              <div>
                <StatusDot done={ccmSegments[4]?.completed} /> Time: 80m
              </div>
            </div>
          }
        >
          <BillingProgressRing label="CCM" segments={ccmSegments} isLight={isLight} indicator={ccm} />
        </Tooltip>
      )}
    </div>
  );
};

export default BillingProgressGroup;
